/**
 * Client provider catalog — mirrors shared/providerCatalog.js (AUTH0.IMPL.1 / IMPL.8).
 * Canonical source: shared/providerCatalog.js. Server re-exports shared directly.
 * Vite cannot import CJS shared — alignment enforced by shared/__tests__/providerCatalogAlignment.test.js.
 *
 * @module client/utils/providerContext
 */

import {
  OKTA_FRAMEWORK_CONFIG,
  ENTRA_FRAMEWORK_CONFIG,
  AUTH0_FRAMEWORK_CONFIG,
  ORY_FRAMEWORK_CONFIG,
} from "./providerFrameworks.js";

/** @typedef {import('../../../shared/providerCatalog.js').ProviderMeta} ProviderMeta */

/** @type {Record<string, ProviderMeta>} */
export const PROVIDER_CATALOG = {
  okta: {
    providerId: "okta",
    displayName: "Okta",
    tenantLabel: "Okta",
    methodologyClause: "Okta tenants",
    adminConsoleName: "Okta Admin Console",
    methodologyLine:
      "Methodology: idwiz evaluates Okta tenants against a deterministic rubric mapped to your selected compliance scope.",
    executiveFallback:
      "This assessment summarizes your Okta tenant’s current posture, prioritizing findings that map to your selected compliance scope.",
    unableToEvaluateIntro:
      "The following rules could not be evaluated against this tenant. Common reasons: the API token does not have the required permission scope, " +
      "the relevant Okta API endpoint returned no data in the sampled window, or the rule requires a deeper data fetch not yet performed. " +
      "These are not security findings — they are gaps in our ability to assess. Most are resolved by re-running with a token that has Super Admin scope, " +
      "or by waiting for fresh activity in the tenant.",
    projectHeaderPrefix: "Okta",
    frameworks: OKTA_FRAMEWORK_CONFIG,
  },
  entra: {
    providerId: "entra",
    displayName: "Microsoft Entra",
    tenantLabel: "Microsoft Entra",
    methodologyClause: "Microsoft Entra tenants",
    adminConsoleName: "Microsoft Entra admin center",
    methodologyLine:
      "Methodology: idwiz evaluates Microsoft Entra tenants against a deterministic rubric mapped to your selected compliance scope.",
    executiveFallback:
      "This assessment summarizes your Microsoft Entra tenant’s current posture, prioritizing findings that map to your selected compliance scope.",
    unableToEvaluateIntro:
      "The following rules could not be evaluated against this tenant. Common reasons: Microsoft Graph permission or license limits " +
      "(for example Entra ID P1/P2 for sign-in logs or Conditional Access), missing application scope, or no data in the sampled window. " +
      "These are not security findings — they are gaps in our ability to assess.",
    projectHeaderPrefix: "Entra",
    frameworks: ENTRA_FRAMEWORK_CONFIG,
  },
  auth0: {
    providerId: "auth0",
    displayName: "Auth0",
    tenantLabel: "Auth0",
    methodologyClause: "Auth0 tenants",
    adminConsoleName: "Auth0 Dashboard",
    methodologyLine:
      "Methodology: idwiz evaluates Auth0 tenants against a deterministic rubric mapped to your selected compliance scope.",
    executiveFallback:
      "This assessment summarizes your Auth0 tenant’s current posture, prioritizing findings that map to your selected compliance scope.",
    unableToEvaluateIntro:
      "The following rules could not be evaluated against this tenant. Common reasons: Auth0 Management API permission or plan-tier limits " +
      "(for example Custom Domain, Enterprise Connections, or extended log retention on lower tiers), missing read scopes on the M2M application, " +
      "or no data in the sampled window. These are not security findings — they are gaps in our ability to assess.",
    projectHeaderPrefix: "Auth0",
    frameworks: AUTH0_FRAMEWORK_CONFIG,
  },
  ory: {
    providerId: "ory",
    displayName: "Ory Network",
    tenantLabel: "Ory Network",
    methodologyClause: "Ory Network projects",
    adminConsoleName: "Ory Network Console",
    methodologyLine:
      "Methodology: idwiz evaluates Ory Network projects against a deterministic rubric mapped to your selected compliance scope.",
    executiveFallback:
      "This assessment summarizes your Ory Network project’s current posture, prioritizing findings that map to your selected compliance scope.",
    unableToEvaluateIntro:
      "The following rules could not be evaluated against this project. Common reasons: the Project API key lacks admin access, " +
      "workspace configuration is required for some primitives (deferred to v2), or collectors have not yet run in this release. " +
      "These are not security findings — they are gaps in our ability to assess.",
    postureGapAbsentIntro:
      "These surfaces are not configured yet — typical on a fresh Ory project. They are posture gaps, not passing security controls.",
    postureGapDeferredIntro:
      "These checks need Ory workspace-level configuration (password policy, session policy, flow security). Deferred to v2 — not a green pass.",
    greenfieldFindingsIntro:
      "Fresh tenant baseline — most object surfaces are empty. Review gaps below, then use discovery options to plan identities, apps, and federation.",
    projectHeaderPrefix: "Ory",
    frameworks: ORY_FRAMEWORK_CONFIG,
  },
  orySelfHosted: {
    providerId: "ory",
    displayName: "Ory (self-hosted)",
    tenantLabel: "Ory self-hosted deployment",
    methodologyClause: "Ory self-hosted deployments",
    adminConsoleName: "Ory admin API",
    methodologyLine:
      "Methodology: idwiz evaluates self-hosted Ory deployments against a deterministic rubric mapped to your selected compliance scope and attested module scope.",
    executiveFallback:
      "This assessment summarizes your self-hosted Ory deployment’s current posture, prioritizing findings that map to your selected compliance scope.",
    unableToEvaluateIntro:
      "The following rules could not be evaluated against this deployment. Common reasons: admin API endpoints are unreachable, " +
      "config-derived settings were not uploaded, a module was attested in scope but not connected, or collectors lack visibility. " +
      "These are not security findings — they are gaps in our ability to assess.",
    postureGapAbsentIntro:
      "These surfaces are not configured yet — typical on a fresh deployment. They are posture gaps, not passing security controls.",
    postureGapDeferredIntro:
      "These checks need configuration visibility (upload scrubbed kratos/hydra config or confirm via admin API). Not a green pass.",
    greenfieldFindingsIntro:
      "Fresh deployment baseline — review attested scope, run assessment, and upload config where admin APIs cannot see settings.",
    projectHeaderPrefix: "Ory",
    frameworks: ORY_FRAMEWORK_CONFIG,
  },
  unknown: {
    providerId: "unknown",
    displayName: "Unknown IdP",
    tenantLabel: "identity",
    methodologyClause: "identity tenants",
    adminConsoleName: "admin console",
    methodologyLine:
      "Methodology: idwiz evaluates identity tenants against a deterministic rubric mapped to your selected compliance scope.",
    executiveFallback:
      "This assessment summarizes your tenant’s current posture, prioritizing findings that map to your selected compliance scope.",
    unableToEvaluateIntro:
      "The following rules could not be evaluated against this tenant. Common reasons: missing API permissions, plan or license limits, " +
      "or no data in the sampled window. These are not security findings — they are gaps in our ability to assess.",
    projectHeaderPrefix: "IdP",
    frameworks: ENTRA_FRAMEWORK_CONFIG,
  },
};

export const KNOWN_PROVIDER_IDS = ["okta", "entra", "auth0", "ory"];

/**
 * @param {{ providerId?: string, provider_id?: string } | string | null | undefined} ctx
 */
export function resolveProviderId(ctx) {
  const raw = typeof ctx === "string" ? ctx : ctx?.providerId ?? ctx?.provider_id;
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim().toLowerCase();
  if (s === "entra" || s === "okta" || s === "auth0" || s === "ory") return s;
  return "unknown";
}

/**
 * @param {string | null | undefined} providerId
 */
export function getProviderMeta(providerId) {
  const id = resolveProviderId({ providerId });
  return PROVIDER_CATALOG[id] || PROVIDER_CATALOG.unknown;
}

/**
 * Provider copy keyed by project (Ory self-hosted vs Network).
 *
 * @param {{ providerId?: string, provider_id?: string, credentialMetadata?: { deployment_mode?: string } | null } | null | undefined} project
 */
export function getProviderMetaForProject(project) {
  const id = resolveProviderId(project);
  if (id === "ory" && String(project?.credentialMetadata?.deployment_mode || "") === "self_hosted") {
    return PROVIDER_CATALOG.orySelfHosted;
  }
  return getProviderMeta(id);
}

export {
  normalizeProviderComplianceFrameworks,
  getDefaultProviderComplianceFrameworks,
} from "./providerFrameworks.js";
